import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Send, Pencil, Eye, CheckCircle2, Sparkles } from "lucide-react";

const EMPTY = { progress_summary: "", current_focus: "", next_step: "", patient_message: "" };
const fmt = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

/**
 * SessionReviews — Batch 8E · clinician create/edit/publish/amend of short,
 * patient-facing session summaries. Driven off the patient's real internal
 * appointments. Drafts are clinician-only until explicitly published.
 */
export default function SessionReviews({ patientId }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // appointment_id currently open
  const [form, setForm] = useState(EMPTY);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amend, setAmend] = useState({ reason: "", ...EMPTY });
  const [confirmPublish, setConfirmPublish] = useState(null); // review id
  const [generating, setGenerating] = useState(null);         // appointment id mid AI draft

  const load = async () => {
    try {
      const r = await api.get(`/session-reviews/patient/${patientId}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load session reviews"); }
  };
  useEffect(() => { load(); }, [patientId]);

  if (!data) return <div className="text-zinc-500 text-sm" data-testid="session-reviews-loading">Loading session reviews…</div>;

  const reviewFor = (apptId) => (data.reviews || []).find((r) => r.appointment_id === apptId);

  const openEditor = (appt, existing) => {
    setEditing(appt.id);
    setForm(existing ? {
      progress_summary: existing.progress_summary || "",
      current_focus: existing.current_focus || "",
      next_step: existing.next_step || "",
      patient_message: existing.patient_message || "",
    } : EMPTY);
    setAmendOpen(false);
  };

  const saveDraft = async (appt, existing) => {
    try {
      if (existing) await api.patch(`/session-reviews/${existing.id}`, form);
      else await api.post(`/session-reviews`, { patient_id: patientId, appointment_id: appt.id, ...form });
      toast.success("Draft saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const doPublish = async (id) => {
    try {
      await api.post(`/session-reviews/${id}/publish`);
      toast.success("Published to patient");
      setConfirmPublish(null); setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
  };

  // Batch 10B · clinician-triggered AI DRAFT (completed appointments only).
  // Never publishes; populates the normal editable draft for review + edit.
  const genDraft = async (appt, existing) => {
    const hasContent = existing && ["progress_summary", "current_focus", "next_step", "patient_message"].some((k) => existing[k]);
    if (hasContent && !window.confirm("Replace the current draft with a fresh AI draft? Your existing text will be overwritten.")) return;
    setGenerating(appt.id);
    try {
      const r = await api.post(`/session-reviews/generate`, {
        patient_id: patientId, appointment_id: appt.id,
        review_id: existing?.id, confirm_regenerate: !!hasContent,
      });
      toast.success("AI draft generated — review before publishing");
      await load();
      openEditor(appt, r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate AI draft"); }
    finally { setGenerating(null); }
  };

  const doAmend = async (id) => {
    if (!amend.reason.trim()) return toast.error("A short amendment reason is required");
    const payload = { reason: amend.reason };
    ["progress_summary", "current_focus", "next_step", "patient_message"].forEach((k) => { if (amend[k] !== "") payload[k] = amend[k]; });
    try {
      await api.post(`/session-reviews/${id}/amend`, payload);
      toast.success("Amendment saved");
      setAmendOpen(false); setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not amend"); }
  };

  const Field = ({ label, value, onChange, placeholder, testid }) => (
    <div>
      <div className="fl-label mb-1">{label}</div>
      <textarea className="fl-input" rows={2} value={value} placeholder={placeholder} onChange={onChange} data-testid={testid} />
    </div>
  );

  const appts = (data.appointments || []).filter((a) => !["cancelled", "declined"].includes(a.status));

  return (
    <div className="mt-6 border-t border-white/5 pt-5" data-testid="session-reviews">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="text-[#f47a20]" size={16} />
        <div className="fl-heading text-lg">Session reviews</div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Short patient-facing summaries. Drafts stay private until you publish. Separate from Assessments &amp; Notes.</p>

      {appts.length === 0 && <div className="text-sm text-zinc-500" data-testid="session-reviews-empty">No eligible appointments yet.</div>}

      <div className="space-y-2">
        {appts.map((appt) => {
          const rv = reviewFor(appt.id);
          const isEditing = editing === appt.id;
          return (
            <div key={appt.id} className="rounded-xl border border-white/10 p-3" data-testid={`session-review-appt-${appt.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{appt.service || "Session"}</div>
                  <div className="text-[11px] text-zinc-500">{fmt(appt.scheduled_at)} · {appt.status}</div>
                </div>
                <div className="flex items-center gap-2">
                  {rv?.status === "published" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "rgba(34,197,94,.15)", color: "#22c55e" }} data-testid={`sr-badge-${appt.id}`}>Published</span>}
                  {rv?.status === "draft" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "rgba(245,158,11,.15)", color: "#f59e0b" }} data-testid={`sr-badge-${appt.id}`}>Draft</span>}
                  {!rv && <button className="fl-btn-ghost text-xs" onClick={() => openEditor(appt, null)} data-testid={`sr-create-${appt.id}`}><Pencil size={12} /> Create session review</button>}
                  {appt.status === "completed" && rv?.status !== "published" && (
                    <button className="fl-btn-ghost text-xs" onClick={() => genDraft(appt, rv)} disabled={generating === appt.id} data-testid={`sr-generate-${appt.id}`}>
                      <Sparkles size={12} /> {generating === appt.id ? "Generating…" : (rv ? "Regenerate AI draft" : "Generate AI draft")}
                    </button>
                  )}
                  {rv?.status === "draft" && <button className="fl-btn-ghost text-xs" onClick={() => openEditor(appt, rv)} data-testid={`sr-continue-${appt.id}`}><Pencil size={12} /> Continue</button>}
                  {rv?.status === "published" && <button className="fl-btn-ghost text-xs" onClick={() => openEditor(appt, rv)} data-testid={`sr-view-${appt.id}`}><Eye size={12} /> View</button>}
                </div>
              </div>

              {rv?.case_title && <div className="mt-1 text-[11px] text-[#f8a766]">{rv.case_title}</div>}

              {/* Draft editor */}
              {isEditing && rv?.status !== "published" && (
                <div className="mt-3 space-y-3" data-testid={`sr-editor-${appt.id}`}>
                  {rv?.draft_source === "ai" && (
                    <div className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: "color-mix(in oklab, var(--fl-orange) 12%, transparent)", color: "#f8a766" }} data-testid={`sr-ai-label-${appt.id}`}>
                      <Sparkles size={11} /> AI draft — review before publishing
                    </div>
                  )}
                  <Field label="Progress since last review" value={form.progress_summary} placeholder="e.g. Your knee is tolerating loading better and your squat control has improved." onChange={(e) => setForm({ ...form, progress_summary: e.target.value })} testid={`sr-progress-${appt.id}`} />
                  <Field label="Current focus" value={form.current_focus} placeholder="e.g. Building quadriceps strength and single-leg control." onChange={(e) => setForm({ ...form, current_focus: e.target.value })} testid={`sr-focus-${appt.id}`} />
                  <Field label="Next step" value={form.next_step} placeholder="e.g. Continue the updated programme; we'll reassess running tolerance next review." onChange={(e) => setForm({ ...form, next_step: e.target.value })} testid={`sr-next-${appt.id}`} />
                  <Field label="Optional message" value={form.patient_message} placeholder="Optional clarification for the patient." onChange={(e) => setForm({ ...form, patient_message: e.target.value })} testid={`sr-message-${appt.id}`} />
                  <div className="flex flex-wrap gap-2">
                    <button className="fl-btn text-xs" onClick={() => saveDraft(appt, rv)} data-testid={`sr-save-${appt.id}`}>Save draft</button>
                    {rv?.id && <button className="fl-btn text-xs" onClick={() => setConfirmPublish(rv.id)} data-testid={`sr-publish-${appt.id}`}><Send size={12} /> Publish to patient</button>}
                    <button className="fl-btn-ghost text-xs" onClick={() => setEditing(null)}>Close</button>
                  </div>
                </div>
              )}

              {/* Published read-only view */}
              {isEditing && rv?.status === "published" && !amendOpen && (
                <div className="mt-3 space-y-2 text-sm" data-testid={`sr-published-${appt.id}`}>
                  {rv.progress_summary && <div><div className="fl-label">Progress</div><div className="text-zinc-300">{rv.progress_summary}</div></div>}
                  {rv.current_focus && <div><div className="fl-label">Current focus</div><div className="text-zinc-300">{rv.current_focus}</div></div>}
                  {rv.next_step && <div><div className="fl-label">Next step</div><div className="text-zinc-300">{rv.next_step}</div></div>}
                  {rv.patient_message && <div><div className="fl-label">Message</div><div className="text-zinc-300">{rv.patient_message}</div></div>}
                  <div className="text-[11px] text-zinc-500">Published {fmt(rv.published_at)}{(rv.amendments || []).length ? ` · amended ${rv.amendments.length}×` : ""}</div>
                  <div className="flex gap-2">
                    <button className="fl-btn-ghost text-xs" onClick={() => { setAmend({ reason: "", progress_summary: rv.progress_summary || "", current_focus: rv.current_focus || "", next_step: rv.next_step || "", patient_message: rv.patient_message || "" }); setAmendOpen(true); }} data-testid={`sr-amend-open-${appt.id}`}><Pencil size={12} /> Amend review</button>
                    <button className="fl-btn-ghost text-xs" onClick={() => setEditing(null)}>Close</button>
                  </div>
                </div>
              )}

              {/* Amend form */}
              {isEditing && rv?.status === "published" && amendOpen && (
                <div className="mt-3 space-y-3" data-testid={`sr-amend-form-${appt.id}`}>
                  <Field label="Amendment reason · required" value={amend.reason} placeholder="Why is this being corrected?" onChange={(e) => setAmend({ ...amend, reason: e.target.value })} testid={`sr-amend-reason-${appt.id}`} />
                  <Field label="Progress since last review" value={amend.progress_summary} onChange={(e) => setAmend({ ...amend, progress_summary: e.target.value })} testid={`sr-amend-progress-${appt.id}`} />
                  <Field label="Current focus" value={amend.current_focus} onChange={(e) => setAmend({ ...amend, current_focus: e.target.value })} testid={`sr-amend-focus-${appt.id}`} />
                  <Field label="Next step" value={amend.next_step} onChange={(e) => setAmend({ ...amend, next_step: e.target.value })} testid={`sr-amend-next-${appt.id}`} />
                  <Field label="Optional message" value={amend.patient_message} onChange={(e) => setAmend({ ...amend, patient_message: e.target.value })} testid={`sr-amend-message-${appt.id}`} />
                  <div className="flex gap-2">
                    <button className="fl-btn text-xs" onClick={() => doAmend(rv.id)} data-testid={`sr-amend-save-${appt.id}`}>Save amendment</button>
                    <button className="fl-btn-ghost text-xs" onClick={() => setAmendOpen(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Publish confirmation */}
              {confirmPublish && confirmPublish === rv?.id && isEditing && (
                <div className="mt-3 rounded-lg border border-[#f47a20]/40 p-3" data-testid={`sr-publish-confirm-${appt.id}`}>
                  <div className="text-sm text-zinc-200">Publish session review?</div>
                  <p className="text-xs text-zinc-500 mt-1">This summary will become visible to the patient.</p>
                  <div className="flex gap-2 mt-2">
                    <button className="fl-btn text-xs" onClick={() => doPublish(rv.id)} data-testid={`sr-publish-confirm-btn-${appt.id}`}><CheckCircle2 size={12} /> Publish</button>
                    <button className="fl-btn-ghost text-xs" onClick={() => setConfirmPublish(null)}>Keep editing</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
